/**
 * Centralized gate for "who is this reservation for?" before creating a new
 * draft. Group admins, platform admins, and personal admins (RM-150) all
 * need a host picker before a reservation can be drafted.
 *
 * Personal admins are NEVER allowed to book for themselves: a user whose only
 * booking authority comes from being a personal admin sees only the members
 * they admin for in the picker. A user who is also Platinum / a group admin
 * sees themselves alongside those members.
 */

const SELF_ELIGIBLE_PRIMARY_PERMISSIONS = new Set(['admin', 'owner'])
const GROUP_ADMIN_PERMISSIONS = new Set(['admin', 'adminStandard', 'owner'])

function isPlatformAdmin(myUser) {
  return myUser?.userLevel === 'admin'
}

function isGroupAdminLike(myUser) {
  return GROUP_ADMIN_PERMISSIONS.has(myUser?.primaryGroupPermission)
    && myUser?.primaryGroupType !== 'restaurant'
}

// Delegate relationships that require choosing whose reservation it is:
// personal-admin (RM-150) and spouse (RM-159, Tier 1 submit-on-behalf).
function hasDelegateRelationship(canActForMembers) {
  return (canActForMembers || []).some(m => m.relationship === 'personal-admin' || m.relationship === 'spouse')
}

/**
 * True when the host picker dialog must be shown before drafting the reservation.
 * Platform admins normally skip this (they use the AdminDirectReservationDialog
 * autocomplete to book for any member) — EXCEPT when they have a personal Platinum
 * spouse, in which case they get the picker too so they can choose "self" vs
 * "Submit for <spouse>" just like any member (RM-159).
 */
export function needsHostSelection({ myUser, canActForMembers }) {
  if (!myUser?.email) return false
  if (myUser.userLevel === 'dummy') return false
  if (isPlatformAdmin(myUser)) return hasDelegateRelationship(canActForMembers)
  if (isGroupAdminLike(myUser)) return true

  return hasDelegateRelationship(canActForMembers)
}

/**
 * Compute the deduplicated list of hosts the actor can book on behalf of.
 * Each entry: { text, value (email), source, profileIncomplete }.
 *   source ∈ { 'self', 'group', 'personal-admin' }
 *
 * Self is included only when the actor has standalone booking authority
 * (their own Platinum membership, or a non-adminStandard group permission).
 * This is what excludes self for personal-admin-only users.
 */
export function getEligibleHosts({ myUser, userGroups, canActForMembers }) {
  if (!myUser?.email) return []

  const byEmail = new Map()
  const meEmailLower = myUser.email.toLowerCase()
  const meIsPlatinum = !!myUser.isPlatinum
  const meIsPrimaryGroupAdmin = SELF_ELIGIBLE_PRIMARY_PERMISSIONS.has(myUser.primaryGroupPermission)
    && myUser.primaryGroupType !== 'restaurant'

  if (meIsPlatinum || meIsPrimaryGroupAdmin) {
    // Show the self option as "Me (Full Name)" so it matches the format of the
    // other host options (all full names) and surfaces the actual name the
    // restaurant sees. Falls back to plain "Me" if the name isn't populated.
    const meFullName = [myUser.firstName, myUser.lastName].filter(Boolean).join(' ').trim()
    byEmail.set(meEmailLower, {
      text: meFullName ? `Me (${meFullName})` : 'Me',
      value: myUser.email,
      source: 'self',
      profileIncomplete: false,
    })
  }

  // Group members — ONLY when the actor is an owner/admin of that specific group.
  // A regular group member must NOT be able to book for co-members just because
  // they share a corporate group. Before RM-159 only group admins ever reached
  // this function (needsHostSelection gated on it); once spouses began opening the
  // picker for non-admin members, this unguarded loop wrongly exposed the whole
  // group (e.g. a Shore Capital member seeing all of Shore). Mirrors the backend
  // /me/can-act-for group-admin query: non-friends groups where the actor holds
  // owner / admin / adminStandard.
  //
  // RM-319: and only a member the server lets the actor book for — /me/can-act-for
  // lists them (the group-admin rule: never a restaurant team, never a member a
  // group admin added, never an RM admin). Anyone else would be refused when the
  // draft is created. Platform admins can book for anyone and skip this.
  const serverAllowed = new Set((canActForMembers || [])
    .map(m => (m.email || '').toLowerCase())
    .filter(Boolean))

  ;(userGroups || []).forEach(group => {
    if (group.type === 'friends') return
    const myPermission = (group.userPermissions || [])
      .find(p => (p.email || '').toLowerCase() === meEmailLower)?.permission
    if (!GROUP_ADMIN_PERMISSIONS.has(myPermission)) return

    ;(group.userPermissions || []).forEach(p => {
      if (!p.email) return
      if (p.permission === 'pending' || p.permission === 'adminStandard') return
      const key = p.email.toLowerCase()
      if (byEmail.has(key)) return
      const isSelf = key === meEmailLower
      if (!isSelf && !isPlatformAdmin(myUser) && !serverAllowed.has(key)) return
      const hasFullName = p.firstName && p.lastName
      const profileIncomplete = !isSelf && (!hasFullName || !p.phoneNumber)
      const baseText = hasFullName
        ? `${p.firstName} ${p.lastName}`
        : (isSelf ? 'Myself' : p.email)
      const text = profileIncomplete ? `${baseText}*` : baseText
      byEmail.set(key, {
        text,
        value: p.email,
        source: isSelf ? 'self' : 'group',
        profileIncomplete,
      })
    })
  })

  // Personal-admin members from /me/can-act-for
  ;(canActForMembers || []).forEach(m => {
    if (m.relationship !== 'personal-admin' || !m.email) return
    const key = m.email.toLowerCase()
    if (key === meEmailLower) return // never include self via personal-admin path
    if (byEmail.has(key)) return
    const hasFullName = m.firstName && m.lastName
    byEmail.set(key, {
      text: hasFullName ? `${m.firstName} ${m.lastName}` : m.email,
      value: m.email,
      source: 'personal-admin',
      profileIncomplete: false,
    })
  })

  // Spouse members from /me/can-act-for (RM-159, Tier 1). Spouse outranks any
  // other relationship for the same email, so this overrides existing entries.
  // Shown as a plain host name (no "Submit for" prefix) — consistent with the
  // self / group / personal-admin entries and the admin host autocomplete.
  ;(canActForMembers || []).forEach(m => {
    if (m.relationship !== 'spouse' || !m.email) return
    const key = m.email.toLowerCase()
    if (key === meEmailLower) return
    const hasFullName = m.firstName && m.lastName
    byEmail.set(key, {
      text: hasFullName ? `${m.firstName} ${m.lastName}` : m.email,
      value: m.email,
      source: 'spouse',
      profileIncomplete: false,
    })
  })

  return [...byEmail.values()].sort((a, b) => a.text.localeCompare(b.text))
}
